import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { stringSorter } from "../../utils/functions.js";
import DataTable from "../../components/CustomDatatable/index.js";
import {
  Button,
  Card,
  Space,
  Typography,
  Breadcrumb,
  Row,
  Col,
  Popconfirm,
} from "antd";
import {
  EditOutlined,
  HomeOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { Spinner } from "reactstrap";
import BrandsForm from "./BrandsForm.js";
import { enhanceColumnsWithFilters } from "../../components/ColumnFilters/index.js";
import { addBrand, deleteBrand, getBrands, updateBrand, updateBrandStatus } from "../../store/actions/brand.js";
import moment from "moment-timezone";
import CustomToggleSwitch from "../../components/CustomToggleSwitch.js";

const { Title } = Typography;

const Brands = () => {
  document.title = "Brands";
  const [isLoading, setIsLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  const dispatch = useDispatch();
  const brand = useSelector((state) => state.brand.list);

  useEffect(() => {
    const fetchBrands = async () => {
      try {
        await dispatch(getBrands());
      } catch (error) {
        console.error("Failed to fetch brands:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchBrands();
  }, [dispatch]);

  // ─── ADD / UPDATE ────────────────────────────────────────────────────────────
  const handleCreate = async (values) => {
    if (selectedRecord) {
      // Collect only changed fields
      const changedFields = {};

      if (values.name !== selectedRecord.name)
        changedFields.name = values.name;

      if ((values.printname ?? "") !== (selectedRecord.printname ?? ""))
        changedFields.printname = values.printname ?? "";

      if ((values.ordinal ?? "") !== (selectedRecord.ordinal ?? ""))
        changedFields.ordinal = values.ordinal ?? "";

      if ((values.isForAll ?? false) !== (selectedRecord.isForAll ?? false))
        changedFields.isForAll = values.isForAll ?? false;

      // Nothing changed — skip API call
      if (Object.keys(changedFields).length === 0) {
        setIsModalVisible(false);
        setSelectedRecord(null);
        return;
      }

      await dispatch(updateBrand({ id: Number(selectedRecord.id), ...changedFields }));
    } else {
      // ADD — plain JSON payload
      const payload = {
        name: values.name,
        isForAll: values.isForAll ?? false,
        ...(values.printname && { printname: values.printname }),
        ...(values.ordinal !== undefined && values.ordinal !== null && { ordinal: Number(values.ordinal) }),
      };
      await dispatch(addBrand(payload));
    }

    await dispatch(getBrands());
    setIsModalVisible(false);
    setSelectedRecord(null);
  };

  // ─── DELETE ──────────────────────────────────────────────────────────────────
  const handleDelete = async (id) => {
    await dispatch(deleteBrand(id));
    await dispatch(getBrands());
  };

  // ─── STATUS TOGGLE ───────────────────────────────────────────────────────────
  const handleChangeStatus = async (id, status) => {
    await dispatch(updateBrandStatus(id, status));
    await dispatch(getBrands());
  };

  // ─── OPEN EDIT MODAL ─────────────────────────────────────────────────────────
  const handleEdit = (record) => {
    setSelectedRecord(record);
    setIsModalVisible(true);
  };

  // ─── CLOSE MODAL ─────────────────────────────────────────────────────────────
  const handleCancel = () => {
    setIsModalVisible(false);
    setSelectedRecord(null);
  };

  const renderCell = (text) => (text ? text : "N/A");

  let columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: stringSorter("name"),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Print Name",
      dataIndex: "printname",
      key: "printname",
      sorter: stringSorter("printname"),
      sortDirections: ["ascend", "descend"],
      render: renderCell,
    },
    {
      title: "Ordinal",
      dataIndex: "ordinal",
      key: "ordinal",
      render: renderCell,
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) =>
        moment(date).isValid() ? moment(date).format("YYYY-MM-DD HH:mm:ss") : "",
      sorter: (a, b) =>
        moment(a.createdAt).isValid() && moment(b.createdAt).isValid()
          ? moment(a.createdAt).unix() - moment(b.createdAt).unix()
          : 0,
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status, record) => (
        <CustomToggleSwitch
          record={record}
          onChangeStatus={handleChangeStatus}
        />
      ),
      sorter: (a, b) => a.status.localeCompare(b.status),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Space size="middle">
          <Button type="link" onClick={() => handleEdit(record)}>
            <EditOutlined />
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this record?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  columns = enhanceColumnsWithFilters(columns);

  return (
    <div className="page-content">
      <Breadcrumb style={{ marginBottom: "16px" }}>
        <Breadcrumb.Item href="/">
          <HomeOutlined />
        </Breadcrumb.Item>
        <Breadcrumb.Item href="/dashboard">Dashboard</Breadcrumb.Item>
        <Breadcrumb.Item>Brands</Breadcrumb.Item>
      </Breadcrumb>

      <Card>
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Row justify="space-between" align="middle">
            <Col>
              <Title level={2} style={{ margin: 0 }}>
                Manage Brands
              </Title>
            </Col>
            <Col>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setSelectedRecord(null);
                  setIsModalVisible(true);
                }}
              >
                Add Brands
              </Button>
            </Col>
          </Row>

          {isLoading ? (
            <div className="d-flex justify-content-center">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <DataTable
              dataSource={brand.map((item) => ({ ...item, key: item.id }))}
              columns={columns}
            />
          )}
        </Space>
      </Card>

      <BrandsForm
        visible={isModalVisible}
        onCreate={handleCreate}
        onCancel={handleCancel}
        record={selectedRecord}
      />
    </div>
  );
};

export default Brands;